// admin-subscribers.js - Admin "Subscribers" subtab: manage the newsletter / digest
// subscriber list (arbitrary emails, NOT accounts). List + status filter + search,
// manual add/remove, and bulk CSV export. Talks to /api/admin/email/subscribers
// (admin-only). Lazy-loads on first tab open; the generic .atab handler in
// admin-tickets.js shows/hides #apanel-subscribers.
import { getUser } from './auth.js';

function token() { return localStorage.getItem('gdbs_token') || ''; }

async function api(method, path, body) {
  const headers = { 'Content-Type': 'application/json' };
  if (token()) headers['Authorization'] = 'Bearer ' + token();
  const res = await fetch(path, { method, headers, body: body ? JSON.stringify(body) : undefined });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `${method} ${path}: ${res.status}`);
  return json;
}

function esc(s) {
  if (s === null || s === undefined) return '';
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', '\'': '&#39;' }[c]));
}
function isAdmin() { try { return getUser()?.role === 'admin'; } catch { return false; } }
function fmtDate(iso) { if (!iso) return '-'; try { return new Date(iso).toLocaleDateString(); } catch { return iso; } }

const BADGE = {
  confirmed:    'background:#16a34a22;color:#16a34a;border:1px solid #16a34a66',
  pending:      'background:#d9770622;color:#d97706;border:1px solid #d9770666',
  unsubscribed: 'background:#9ca3af2e;color:#6b7280;border:1px solid #9ca3af66',
  bounced:      'background:#dc262622;color:#dc2626;border:1px solid #dc262666',
};

let counts = { total: 0, confirmed: 0, pending: 0, unsubscribed: 0 };
let rows = [];
let fStatus = '';
let fQuery = '';
let loaded = false;

function root() { return document.getElementById('subscribers-admin'); }

function render() {
  const el = root(); if (!el) return;
  const chip = (label, n, val) =>
    `<button class="sub-filter btn btn-sm${fStatus === val ? ' btn-primary' : ''}" data-status="${val}">${label}: <b>${n}</b></button>`;
  const body = rows.map(s => `
    <tr>
      <td style="font-family:monospace;">${esc(s.email)}</td>
      <td>${esc(s.name) || '<span style="color:var(--text-dim)">-</span>'}</td>
      <td><span style="padding:1px 9px;border-radius:9999px;font-size:11px;font-weight:600;${BADGE[s.status] || BADGE.pending}">${esc(s.status)}</span></td>
      <td style="color:var(--text-dim);font-size:11px;">${esc(s.source)}</td>
      <td style="color:var(--text-dim);font-size:11px;">${fmtDate(s.createdAt)}</td>
      <td><button class="sub-del btn btn-sm" data-id="${esc(s.id)}" title="Remove" style="color:#dc2626;">&times;</button></td>
    </tr>`).join('');

  el.innerHTML = `
    <div style="display:flex;gap:8px;flex-wrap:wrap;margin-bottom:14px;align-items:center;">
      ${chip('All', counts.total, '')}
      ${chip('Confirmed', counts.confirmed, 'confirmed')}
      ${chip('Pending', counts.pending, 'pending')}
      ${chip('Unsubscribed', counts.unsubscribed, 'unsubscribed')}
      <span style="flex:1"></span>
      <button id="sub-export" class="btn btn-sm"><i class="fas fa-download"></i> Export CSV</button>
    </div>
    <div style="display:flex;gap:8px;flex-wrap:wrap;margin-bottom:14px;align-items:center;">
      <input id="sub-search" type="search" placeholder="Search email or name..." value="${esc(fQuery)}" style="flex:1;min-width:200px;">
      <form id="sub-add" style="display:flex;gap:6px;flex-wrap:wrap;align-items:center;">
        <input id="sub-add-email" type="email" placeholder="email to add" required style="min-width:180px;">
        <input id="sub-add-name" type="text" placeholder="name (optional)" style="min-width:120px;">
        <button type="submit" class="btn btn-sm btn-primary"><i class="fas fa-plus"></i> Add</button>
      </form>
    </div>
    <div style="overflow:auto;">
      <table class="admin-table" style="width:100%;">
        <thead><tr><th>Email</th><th>Name</th><th>Status</th><th>Source</th><th>Added</th><th></th></tr></thead>
        <tbody>${body || `<tr><td colspan="6" style="color:var(--text-dim);padding:16px;">No subscribers${(fStatus || fQuery) ? ' match this filter' : ' yet'}.</td></tr>`}</tbody>
      </table>
    </div>
    <p style="color:var(--text-dim);font-size:11px;margin-top:10px;">Subscribers are arbitrary emails (not accounts). Public signups confirm via double opt-in; admin-added entries are confirmed immediately. Emails are stored encrypted at rest.</p>`;
  wire();
}

function wire() {
  const el = root(); if (!el) return;
  el.querySelectorAll('.sub-filter').forEach(b =>
    b.addEventListener('click', () => { fStatus = b.getAttribute('data-status'); load(); }));

  const search = el.querySelector('#sub-search');
  if (search) {
    let t;
    search.addEventListener('input', () => { clearTimeout(t); t = setTimeout(() => { fQuery = search.value.trim(); load(); }, 300); });
  }

  el.querySelector('#sub-add')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const email = el.querySelector('#sub-add-email').value.trim();
    const name = el.querySelector('#sub-add-name').value.trim();
    if (!email) return;
    try { await api('POST', '/api/admin/email/subscribers', { email, name }); await load(); }
    catch (err) { alert(err.message || 'Add failed'); }
  });

  el.querySelectorAll('.sub-del').forEach(b => b.addEventListener('click', async () => {
    const id = b.getAttribute('data-id');
    if (!confirm('Remove this subscriber?')) return;
    try { await api('DELETE', '/api/admin/email/subscribers/' + encodeURIComponent(id)); await load(); }
    catch (err) { alert(err.message || 'Remove failed'); }
  }));

  el.querySelector('#sub-export')?.addEventListener('click', exportCsv);
}

// Authenticated CSV download: fetch with the bearer token, then save the blob.
async function exportCsv() {
  try {
    const headers = {};
    if (token()) headers['Authorization'] = 'Bearer ' + token();
    const res = await fetch('/api/admin/email/subscribers/export', { headers });
    if (!res.ok) throw new Error('Export failed (' + res.status + ')');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'gdbs-subscribers.csv';
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (err) { alert(err.message || 'Export failed'); }
}

async function load() {
  if (!isAdmin()) return;
  const el = root(); if (!el) return;
  try {
    const qs = new URLSearchParams();
    if (fStatus) qs.set('status', fStatus);
    if (fQuery) qs.set('q', fQuery);
    const res = await api('GET', '/api/admin/email/subscribers' + (qs.toString() ? '?' + qs.toString() : ''));
    counts = res.counts || counts;
    rows = res.data || [];
    loaded = true;
    render();
  } catch (err) {
    el.innerHTML = `<p style="color:#dc2626;">Could not load subscribers: ${esc(err.message)}</p>`;
  }
}

export function initAdminSubscribers() {
  const tab = document.querySelector('.atab[data-atab="subscribers"]');
  if (!tab) return;
  tab.addEventListener('click', () => { if (!loaded) load(); else render(); });
}
